import { TextLink } from '@civilgrid/ui/components';
import { each } from 'lodash';
import type { ReactNode } from 'react';
import { useSnapshot } from 'valtio';
import InfoTooltip from '~/components2/InfoTooltip';
import { isConnectbaseCovered } from './common';
import { ProgressCard, useRequestProgressData } from './common/progressData';
import store from './store';
import { useUtilitiesPageRedesignEnabled } from './useUtilitiesPageRedesignEnabled';

const AVAILABLE_DIGITALLY_TOOLTIP =
  'Number of agencies with digital data on CivilGrid, meaning you can view it in the live map or download to CAD/GIS.';

const CONNECTBASE_AVAILABLE_DIGITALLY_NOTE = 'Includes carriers with live data on the map.';

export default function Stats() {
  const { agencies, connectbaseCoverageEnabled } = useSnapshot(store) as typeof store;
  const isPageRedesignEnabled = useUtilitiesPageRedesignEnabled();

  const agencyProgressData = useRequestProgressData();

  const agencyNames = new Set<string>();
  const assetTypes = new Set<string>();
  let availableDigitally = 0;

  each(agencies, (v) => {
    agencyNames.add(v.agencyName);
    assetTypes.add(v.type);
    // A flag-gated Connectbase route counts as digital availability here, matching the Live Map cell.
    if (v.availableDigitally || isConnectbaseCovered(v, connectbaseCoverageEnabled)) availableDigitally++;
  });
  const availableForRequest = Object.keys(agencies).length - availableDigitally;

  // Covered carriers count toward this pill, so the tooltip says so.
  const availableDigitallyTooltip = connectbaseCoverageEnabled
    ? `${AVAILABLE_DIGITALLY_TOOLTIP} ${CONNECTBASE_AVAILABLE_DIGITALLY_NOTE}`
    : AVAILABLE_DIGITALLY_TOOLTIP;

  const pill = (title: string, value: ReactNode, tooltip?: ReactNode, testId?: string) => (
    <div
      key={title}
      data-testid={testId}
      className="flex flex-col rounded-lg border border-grey-30 bg-secondary-light/30 p-4 text-left"
    >
      <div className="text-2xl font-medium">{value}</div>
      <div className="flex space-x-1 text-base">
        <span>{title}</span>
        {tooltip && <InfoTooltip text={tooltip} label={`About ${title}`} />}
      </div>
    </div>
  );

  return (
    <div className="mb-4 grid grid-cols-2 gap-2" data-testid="utilities-stats">
      {/* <ProgressCard
        title={'Site Research Progress'}
        items={researchProgressData}
        tooltip={
          'Site research progress for available agencies. Completed/initiated are all assets that have been requested or are available digitally on the map. Site research available indicates agencies that are available for map/as-built requests but have not been requested.'
        }
      /> */}

      {pill(
        'Utility Agencies',
        agencyNames.size,
        'List of unique utility agencies within the project boundaries. Note that some entities may appear multiple times in the list due to differences in legal or operating names, or slight differences in how they are reported in various databases. Certain datasets contain both \u201cOwner\u201d and \u201cOperator\u201d fields - right-click to view Layer Attributes.',
        'utilities-stat-utility-agencies',
      )}
      {pill(
        'Asset Types',
        assetTypes.size,
        <div>
          <div>Number of unique types of utility assets.</div>
          <br />
          {Array.from(assetTypes)
            .sort()
            .map((a) => (
              <div key={String(a)}>{a}</div>
            ))}
        </div>,
        'utilities-stat-asset-types',
      )}
      {pill('Available Digitally', availableDigitally, availableDigitallyTooltip, 'utilities-stat-available-digitally')}
      {pill(
        'Available for Request',
        availableForRequest,
        'Number of agencies where you can request Maps and as-builts through our premium CivilSearch service. CivilGrid will handle utility requests on your behalf, managing the entire process from start to finish.',
        'utilities-stat-available-for-request',
      )}

      <ProgressCard
        data-testid="utilities-stat-data-request-progress"
        title={isPageRedesignEnabled ? 'Utility Research Progress' : 'Data Request Progress'}
        items={agencyProgressData}
        tooltip={
          <>
            The number of complete, in-progress, or available {isPageRedesignEnabled ? 'utility research' : 'data'}{' '}
            requests for applicable agencies. Agencies whose requests were all cancelled or dismissed appear as a grey
            segment.
            {isPageRedesignEnabled && ' '}
            {/* The components2 table keeps this link in its Availability/Status header tooltip instead. */}
            {isPageRedesignEnabled && (
              <TextLink
                href="https://civilgrid.help.usepylon.com/articles/8372097607-request-status-utility-presence-and-outcome"
                target="_blank"
                data-testid="utilities-request-status-help-link"
              >
                Learn what each status means
                <span aria-hidden="true"> ↗</span>
                <span className="sr-only"> (opens in a new tab)</span>
              </TextLink>
            )}
          </>
        }
      />
    </div>
  );
}
